import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Megaphone, Loader, Circle } from "lucide-react";
import { lmsGetStudentNotices, lmsMarkNoticeRead } from "../api/lmsApi";
import StudentChat from "./StudentChat";
import "../lms.css";

const getStudentData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const studentId = ud.student_id || ud.id || null;
    return { studentId };
  } catch {
    return { studentId: null };
  }
};

// Combined Notice Board / Chat page — announcements on the left (no
// conversation attached, those are one-way broadcasts), and the standalone
// 1:1 Chat panel embedded on the right.
export default function StudentNoticeBoard() {
  const { studentId } = getStudentData();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (studentId) fetchNotices();
  }, [studentId]);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const res = await lmsGetStudentNotices(studentId);
      setNotices(res.data?.data || []);
    } catch (err) {
      console.error("fetchNotices error:", err);
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (notice) => {
    if (notice.is_read) return;
    try {
      await lmsMarkNoticeRead(notice.id, studentId);
      setNotices((prev) => prev.map((n) => (n.id === notice.id ? { ...n, is_read: 1 } : n)));
    } catch (err) {
      console.error("markNoticeRead error:", err);
    }
  };

  return (
    <div>
      <div className="std-page-header" style={{ marginBottom: "20px" }}>
        <h1 className="std-page-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Megaphone className="text-blue-600" size={26} />
          Notice Board / Chat
        </h1>
        <p className="std-page-subtitle">
          Class/batch notices on the left, conversations with your teachers on the right
        </p>
      </div>

      <div className="notice-chat-columns">
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: 700, margin: "0 0 12px" }}>
            <Megaphone className="text-blue-600" size={18} />
            Notice Board
          </h2>

          <div className="std-table-card" style={{ maxHeight: "65vh", overflowY: "auto" }}>
            <table className="std-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Date Posted</th>
              <th>From</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: 40 }}>
                  <Loader size={24} className="ms2-spin" style={{ margin: "0 auto 8px" }} />
                  <div style={{ color: "#64748b", fontSize: 14 }}>Loading notices...</div>
                </td>
              </tr>
            ) : notices.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "48px 20px" }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: "#f1f5f9",
                      color: "#94a3b8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                    }}
                  >
                    <Megaphone size={26} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#334155" }}>No notices yet</div>
                </td>
              </tr>
            ) : (
              notices.map((n) => (
                <tr
                  key={n.id}
                  onClick={() => markRead(n)}
                  style={{
                    background: n.is_read ? "transparent" : "#f0f7ff",
                    cursor: n.is_read ? "default" : "pointer",
                  }}
                >
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {!n.is_read && <Circle size={8} fill="#2563eb" color="#2563eb" />}
                      <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{n.title}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: 13, color: "#64748b" }}>
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 13, color: "#475569" }}>{n.created_by_name || "—"}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
            </table>
          </div>
        </div>

        <div>
          <StudentChat />
        </div>
      </div>
    </div>
  );
}
